import React, { useCallback } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { CATEGORY_CODES } from '_constants/categories';
import { theme } from '_constants/theme';
import { sanitizePhoneNumber } from '_helpers/common';
import { selectCredentialsByCategory, selectUpdating as selectCredentialsUpdating } from '_store/credentials/selector';
import { setCredentials } from '_store/credentials/slice';
import { selectCustomerId, selectDetails, selectUpdating as selectUserUpdating } from '_store/user/selector';
import { updateMe } from '_store/user/slice';

import Card from 'components/shared/layouts/Card';
import Text from 'components/shared/layouts/Text';

import ChangePasswordForm from './ChangePasswordForm';
import ChangeUsernameForm from './ChangeUsernameForm';
import ContactInfoForm from './ContactInfoForm';

const StyledContainer = styled.div`
  padding-top: ${theme.spaces.large}px;

  @media ${theme.devices.tablet} {
    padding-top: ${theme.spaces.regular}px;
    padding-bottom: ${theme.spaces.regular}px;
  }
`;

const StyledCard = styled(Card)`
  margin-bottom: ${theme.spaces.large}px;

  @media ${theme.devices.tablet} {
    margin-bottom: ${theme.spaces.regular}px;
  }
`;

const OrderDetailsView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const userDetails = useSelector(selectDetails);
  const userUpdating = useSelector(selectUserUpdating);
  const credentials = useSelector(selectCredentialsByCategory(CATEGORY_CODES.main));
  const credentialsUpdating = useSelector(selectCredentialsUpdating);

  const { firstName, lastName, email, phone, address } = userDetails || {};

  const handleUpdateUser = useCallback(
    (values) => {
      if (values) {
        const { firstName, lastName, email, phone, ...restValues } = values;
        dispatch(
          updateMe({
            customerId,
            body: { firstName, lastName, email, phone: sanitizePhoneNumber(phone), address: { ...restValues } },
          })
        );
      }
    },
    [customerId]
  );

  const handleChangeUsername = useCallback(
    (values) => {
      if (values) {
        const { username } = values;

        if (credentials) {
          dispatch(setCredentials({ customerId, ...credentials, username }));
        }
      }
    },
    [customerId, credentials]
  );

  const handleChangePassword = useCallback(
    (values) => {
      if (values) {
        const { oldPassword, newPassword } = values;

        if (credentials) {
          dispatch(setCredentials({ customerId, ...credentials, password: newPassword, oldPassword }));
        }
      }
    },
    [customerId, credentials]
  );

  return (
    <StyledContainer>
      <Row>
        <Col xs={12} md={6}>
          <StyledCard>
            <Text size={theme.fontSizes.large2X} weight={700} bottomSpace={theme.spaces.large} desktop>
              Contact Information
            </Text>
            <Text size={theme.fontSizes.large} weight={700} bottomSpace={theme.spaces.large} centered mobile>
              Contact Information
            </Text>
            <ContactInfoForm
              loading={userUpdating}
              defaultValues={{ firstName, lastName, email, phone, ...address }}
              onSubmit={handleUpdateUser}
            />
          </StyledCard>
        </Col>
        <Col xs={12} md={6}>
          <StyledCard>
            <Text size={theme.fontSizes.large2X} weight={700} bottomSpace={theme.spaces.large} desktop>
              Username
            </Text>
            <Text size={theme.fontSizes.large} weight={700} bottomSpace={theme.spaces.large} centered mobile>
              Username
            </Text>
            <ChangeUsernameForm
              loading={credentialsUpdating}
              defaultValues={{ username: credentials?.username }}
              onSubmit={handleChangeUsername}
            />
          </StyledCard>
          <Card>
            <Text size={theme.fontSizes.large2X} weight={700} bottomSpace={theme.spaces.large} desktop>
              New Password
            </Text>
            <Text size={theme.fontSizes.large} weight={700} bottomSpace={theme.spaces.large} centered mobile>
              New Password
            </Text>
            <ChangePasswordForm loading={credentialsUpdating} defaultValues={{}} onSubmit={handleChangePassword} />
          </Card>
        </Col>
      </Row>
    </StyledContainer>
  );
};

export default OrderDetailsView;
